import { transforms } from '@botpress/sdk'
import crypto from 'crypto'
import path from 'path'

import { ParsedIntegration } from '../integrations/types.js'
import { pascalCase, snakeCase } from '../utils/strings.js'
import { ADK_VERSION, relative, toMultilineComment, formatCode } from './utils.js'
import { getIntegrationAlias } from '../utils/ids.js'

const getIntegrationHash = (integration: ParsedIntegration): string => {
  return crypto
    .createHash('sha256')
    .update(
      `${integration.alias}|${integration.definition?.id}|${integration.definition?.version}|${integration.definition?.updatedAt}`
    )
    .digest('hex')
}

const getPascalAlias = (integration: ParsedIntegration) => pascalCase(getIntegrationAlias(integration.alias))

const getIntegrationNames = (integration: ParsedIntegration) => ({
  typings: {
    index: `Integration_All_${getPascalAlias(integration)}`,
    actions: `Integration_Actions_${getPascalAlias(integration)}`,
    channels: `Integration_Channels_${getPascalAlias(integration)}`,
    events: `Integration_Events_${getPascalAlias(integration)}`,
    users: `Integration_Users_${getPascalAlias(integration)}`,
    config: `Integration_Config_${getPascalAlias(integration)}`,
  },
  paths: {
    index: path.join(snakeCase(getPascalAlias(integration)), `index.ts`),
    actions: path.join(snakeCase(getPascalAlias(integration)), `actions.ts`),
    channels: path.join(snakeCase(getPascalAlias(integration)), `channels.ts`),
    events: path.join(snakeCase(getPascalAlias(integration)), `events.ts`),
    users: path.join(snakeCase(getPascalAlias(integration)), `users.ts`),
    config: path.join(snakeCase(getPascalAlias(integration)), `config.ts`),
  },
})

export async function generateIntegrationTypes(integration: ParsedIntegration): Promise<{
  names: ReturnType<typeof getIntegrationNames>
  files: Record<string, string>
}> {
  const names = getIntegrationNames(integration)
  const hash = getIntegrationHash(integration)

  const rel = (to: string): string => {
    const from = names.paths.index
    return relative(from, to).replace(/\.ts$/i, '')
  }

  let tIndex = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition?.name}
  // Version: ${integration.definition?.version}
  // File: ${names.paths.index}
  // Hash: ${hash}
  // Updated at: ${integration.definition?.updatedAt}
  ////////////////////////////////////////////////////////

  import { Integration_Actions_${getPascalAlias(integration)} } from "${rel(names.paths.actions)}";
  import { Integration_Channels_${getPascalAlias(integration)} } from "${rel(names.paths.channels)}";
  import { Integration_Events_${getPascalAlias(integration)} } from "${rel(names.paths.events)}";
  import { Integration_Users_${getPascalAlias(integration)} } from "${rel(names.paths.users)}";
  import { Integration_Config_${getPascalAlias(integration)} } from "${rel(names.paths.config)}";

  export * from "${rel(names.paths.actions)}";
  export * from "${rel(names.paths.channels)}";
  export * from "${rel(names.paths.events)}";
  export * from "${rel(names.paths.users)}";
  export * from "${rel(names.paths.config)}";

  export type Integration_All_${getPascalAlias(integration)} = {
    actions: Integration_Actions_${getPascalAlias(integration)};
    channels: Integration_Channels_${getPascalAlias(integration)};
    events: Integration_Events_${getPascalAlias(integration)};
    users: Integration_Users_${getPascalAlias(integration)};
    config: Integration_Config_${getPascalAlias(integration)};
  };
  `

  let tActions = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition?.name}
  // Version: ${integration.definition?.version}
  // File: ${names.paths.actions}
  // Hash: ${hash}
  // Updated at: ${integration.definition?.updatedAt}
  ////////////////////////////////////////////////////////

  export type Integration_Actions_${getPascalAlias(integration)} = {`

  for (const [name, action] of Object.entries(integration.definition!.actions)) {
    const input = transforms.fromJSONSchema(action.input.schema).toTypescriptType()
    const output = transforms.fromJSONSchema(action.output.schema).toTypescriptType()

    tActions += `
    ${toMultilineComment(`Title: ${action.title || name}\nDescription: ${action.description || 'No description'}`)}
    "${name}": {
      input: ${input};
      output: ${output};
    },\n`
  }

  tActions += `};`

  let tChannels = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition?.name}
  // Version: ${integration.definition?.version}
  // File: ${names.paths.channels}
  // Hash: ${hash}
  // Updated at: ${integration.definition?.updatedAt}
  ////////////////////////////////////////////////////////

  export type Integration_Channels_${getPascalAlias(integration)} = {`

  for (const [channelName, channelDef] of Object.entries(integration.definition?.channels || {})) {
    const convoTagsRecord =
      '{' +
      Object.keys(channelDef.conversation?.tags || {}).reduce((acc, t) => {
        const tag = channelDef.conversation.tags[t]!
        const comment = toMultilineComment(`Title: ${tag?.title}\nDescription: ${tag.description}`)
        // Use ref.fullName to preserve workspace prefix (e.g., "sly/intercom")
        acc += `${comment}\n["${integration.ref.fullName}:${t}"]?: string,\n`
        return acc
      }, '') +
      '}'

    const msgTagsUnion =
      '{' +
      Object.keys(channelDef.message?.tags || {}).reduce((acc, t) => {
        const tag = channelDef.message.tags[t]!
        const comment = toMultilineComment(`Title: ${tag?.title}\nDescription: ${tag.description}`)
        // Use ref.fullName to preserve workspace prefix (e.g., "sly/intercom")
        acc += `${comment}\n["${integration.ref.fullName}:${t}"]?: string,\n`
        return acc
      }, '') +
      '}'

    let messagesBlock = ''
    for (const [messageName, messageDef] of Object.entries(channelDef.messages || {})) {
      const messageType = transforms.fromJSONSchema(messageDef.schema).toTypescriptType()
      messagesBlock += `\n"${messageName}": ${messageType};\n`
    }

    tChannels += `
    ${toMultilineComment(
      `Title: ${channelDef.title || channelName}\nDescription: ${channelDef.description || 'No description'}`
    )}
    "${channelName}": {
      conversation: {
        tags: ${convoTagsRecord};
      };
      message: {
        tags: ${msgTagsUnion};
      };
      messages: {${messagesBlock}
      };
    },`
  }

  tChannels += `
  };
  `

  let tEvents = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition!.name}
  // Version: ${integration.definition!.version}
  // File: ${names.paths.events}
  // Hash: ${hash}
  // Updated at: ${integration.definition!.updatedAt}
  ////////////////////////////////////////////////////////

  export type Integration_Events_${getPascalAlias(integration)} = {`

  for (const [eventName, event] of Object.entries(integration.definition!.events || {})) {
    const payloadType = transforms.fromJSONSchema(event.schema).toTypescriptType()
    const attributesComment = Object.keys(event.attributes || {}).length
      ? `\nAttributes:\n${Object.entries(event.attributes!)
          .map(([k, v]) => `  - ${k}: ${v}`)
          .join('\n')}`.trim()
      : ''

    tEvents += `
    ${toMultilineComment(
      `Title: ${event.title || eventName}\nDescription: ${event.description || 'No description'}${attributesComment}`
    )}
    "${eventName}": ${payloadType};
    `
  }

  tEvents += `
  };
  `

  let tUsers = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition?.name}
  // Version: ${integration.definition?.version}
  // File: ${names.paths.users}
  // Hash: ${hash}
  // Updated at: ${integration.definition?.updatedAt}
  ////////////////////////////////////////////////////////

  export type Integration_Users_${getPascalAlias(integration)} = {`

  const userTagKeys = Object.keys(integration.definition?.user?.tags || {})
  const userTagsUnion = userTagKeys.length
    ? userTagKeys
        .map((t) => {
          const tag = (integration.definition?.user!.tags ?? ({} as any))[t]!
          const comment = toMultilineComment(`Title: ${tag?.title}\nDescription: ${tag?.description}`)
          // Use ref.fullName to preserve workspace prefix (e.g., "sly/intercom")
          return `${comment}\n"${integration.ref.fullName}:${t}"\n`
        })
        .join(' | ')
    : 'never'

  tUsers += `
    tags: ${userTagsUnion};
  };
  `

  // Generate configuration types
  let tConfig = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Integration: ${integration.definition?.name}
  // Version: ${integration.definition?.version}
  // File: ${names.paths.config}
  // Hash: ${hash}
  // Updated at: ${integration.definition?.updatedAt}
  ////////////////////////////////////////////////////////

  export type Integration_Config_${getPascalAlias(integration)} = `

  // Determine which configuration type to use
  const configurationType = integration.configurationType || 'default'

  // Try to get configuration schema from modern .configurations format first
  let configType = '{}'
  if (integration.definition?.configurations && integration.definition.configurations[configurationType]) {
    const configSchema = integration.definition.configurations[configurationType].schema
    if (configSchema) {
      configType = transforms.fromJSONSchema(configSchema).toTypescriptType()
    }
  } else if (integration.definition?.configuration) {
    // Fall back to legacy .configuration format
    const configSchema = integration.definition.configuration.schema
    if (configSchema) {
      configType = transforms.fromJSONSchema(configSchema).toTypescriptType()
    }
  }

  tConfig += `${configType};
  `

  return {
    names,
    files: {
      [names.paths.index]: await formatCode(tIndex),
      [names.paths.actions]: await formatCode(tActions),
      [names.paths.channels]: await formatCode(tChannels),
      [names.paths.events]: await formatCode(tEvents),
      [names.paths.users]: await formatCode(tUsers),
      [names.paths.config]: await formatCode(tConfig),
    },
  }
}
